import { useCallback, useEffect, useState } from 'react';
import { fixedUiTpl } from '@/i18n/fixedUi';
import { useTiktokShopOptional } from '@/tiktok-platform/context/TiktokShopContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { showToast } from '@/utils/toast';
import { beginTiktokManualSyncClick } from '@/utils/tiktokManualSyncClick';
import { toTiktokManualSyncQuotaKind } from '@/utils/tiktokManualSyncQuotaKind';

const EMPTY = {
  used: 0,
  remaining: null,
  limit: 10,
  exhausted: false,
  loading: false,
};

/**
 * 当前店铺某个「同步更新」按钮的每日剩余次数。
 * 按店铺 × 按钮分别计次；一次点击成功只扣 1 次，店铺之间互不占用。
 */
export function useTiktokManualSyncQuota(kind) {
  const shop = useTiktokShopOptional();
  const shopId = shop?.activeShopId || shop?.activeShop?.id || '';
  const buttonKind = toTiktokManualSyncQuotaKind(kind);
  const [quota, setQuota] = useState(EMPTY);

  const reload = useCallback(async () => {
    if (!shopId || !buttonKind) {
      setQuota(EMPTY);
      return EMPTY;
    }
    try {
      const res = await tiktokPlatformAPI.getManualSyncQuota(shopId, buttonKind);
      const limit = Math.max(1, Number(res?.limit) || 10);
      const used = Math.max(0, Number(res?.used) || 0);
      const remaining = Math.max(0, res?.remaining != null ? Number(res.remaining) : limit - used);
      const next = {
        used,
        remaining,
        limit,
        exhausted: remaining <= 0,
        loading: false,
      };
      setQuota(next);
      return next;
    } catch {
      setQuota((prev) => ({ ...prev, loading: false }));
      return null;
    }
  }, [shopId, buttonKind]);

  useEffect(() => {
    setQuota((prev) => ({ ...prev, loading: true }));
    reload();
  }, [reload]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onChange = (ev) => {
      const eventKind = toTiktokManualSyncQuotaKind(ev?.detail?.kind);
      const eventShopId = ev?.detail?.shopId != null ? String(ev.detail.shopId) : '';
      if (eventShopId && shopId && eventShopId !== String(shopId)) return;
      if (eventKind && buttonKind && eventKind !== buttonKind) return;
      reload();
    };
    window.addEventListener('ipe:tiktok-manual-sync-quota', onChange);
    return () => window.removeEventListener('ipe:tiktok-manual-sync-quota', onChange);
  }, [reload, buttonKind, shopId]);

  const assertCanSync = useCallback(() => {
    if (quota.exhausted) {
      showToast(fixedUiTpl('该同步今日已达上限（{0}次），请明天再试', quota.limit), 'warning');
      return false;
    }
    beginTiktokManualSyncClick();
    return true;
  }, [quota.exhausted, quota.limit]);

  return {
    ...quota,
    reload,
    assertCanSync,
  };
}
